import { Application, Container, Point, EventEmitter  } from "pixi.js";

export class ViewportManager extends EventEmitter {
    private app: Application;
    private container: Container;
    private minZoom = 0.1;
    private maxZoom = 5;
    private lastEmitTime: number = 0;
    private emitThrottle: number = 16; // ~60fps

    constructor(app: Application, container: Container) {
        super();

        this.app = app;
        this.container = container;
    }

    public worldToScreen(worldX: number, worldY: number) {
        return {
            x: worldX * this.container.scale.x + this.container.position.x,
            y: worldY * this.container.scale.y + this.container.position.y
        };
    }

    public screenToWorld(screenX: number, screenY: number) {
        return {
            x: (screenX - this.container.position.x) / this.container.scale.x,
            y: (screenY - this.container.position.y) / this.container.scale.y
        };
    }

    public getVisibleBounds() {
        const scale = this.container.scale.x;
        return {
            left: -this.container.position.x / scale,
            top: -this.container.position.y / scale,
            right: (-this.container.position.x + this.app.screen.width) / scale,
            bottom: (-this.container.position.y + this.app.screen.height) / scale,
        };
    }

    public zoom(scale: number, center: Point): void {
        const newScale = this.container.scale.x * scale;

        if (newScale < this.minZoom || newScale > this.maxZoom) return;

        // Store current position before zoom
        const beforeZoom = this.container.toLocal(center);

        // Update scale
        this.container.scale.set(newScale);

        // Update position
        const afterZoom = this.container.toLocal(center);
        this.container.position.x += (afterZoom.x - beforeZoom.x) * newScale;
        this.container.position.y += (afterZoom.y - beforeZoom.y) * newScale;

        // Throttle zoom events
        const now = Date.now();
        if (now - this.lastEmitTime > this.emitThrottle) {
            this.emit('zoomed', { scale: newScale });
            this.lastEmitTime = now;
        }
    }

    public pan(dx: number, dy: number): void {
        // Direct position update
        this.container.position.x += dx;
        this.container.position.y += dy;
    }

    public centerOnWorldPoint(worldX: number, worldY: number): void {
        const scale = this.container.scale.x;
        this.container.position.x = (this.app.screen.width / 2) - (worldX * scale);
        this.container.position.y = (this.app.screen.height / 2) - (worldY * scale);
    }

    public getViewportBounds() {
        return {
            left: -this.container.position.x,
            top: -this.container.position.y,
            right: -this.container.position.x + this.app.screen.width,
            bottom: -this.container.position.y + this.app.screen.height,
        };
    }

    public getCanvas(): HTMLCanvasElement {
        return this.app.canvas as HTMLCanvasElement;
    }
}
